/**
 * Thin wrapper around MUI X DataGrid with sensible defaults:
 *   - density: comfortable
 *   - column sort + multi-column hidden behind the toolbar
 *   - free-text quick filter
 *   - CSV export button
 *   - 25 / 50 / 100 page size choices
 *
 * Pages can override anything by passing through `slotProps` or extra
 * `sx` etc., but in 95% of cases the defaults are enough -- so each
 * page just supplies `rows` and `columns`.
 */

import React from 'react';
import { Box } from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';

const Toolbar = () => (
  <GridToolbarContainer sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <GridToolbarQuickFilter
      debounceMs={250}
      placeholder="Search…"
      sx={{
        flex: 1,
        '& input': { fontSize: 14 },
      }}
    />
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport
      printOptions={{ disableToolbarButton: true }}
      csvOptions={{ utf8WithBom: true }}
    />
  </GridToolbarContainer>
);

export default function DataTable({
  rows,
  columns,
  loading = false,
  getRowId,
  initialState,
  pageSizeOptions = [25, 50, 100],
  height = 560,
  rowHeight,
  ...rest
}) {
  return (
    <Box sx={{ width: '100%', height }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        rowHeight={rowHeight}
        density="comfortable"
        disableRowSelectionOnClick
        slots={{ toolbar: Toolbar }}
        slotProps={{
          toolbar: { showQuickFilter: true },
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
        }}
        pageSizeOptions={pageSizeOptions}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
          ...initialState,
        }}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
        {...rest}
      />
    </Box>
  );
}
